import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { notification } from "antd";
import { requiredRule } from "./useBasicComponent";
import { useResourceContext } from "@/layouts/ResourceContext";
import { useTemplateComponent } from "./useTemplateComponent";
import { CLUSTER_TYPE_ENUM } from "@/constants";
import { getSharedNodegroups } from "@/apis";

export const usePoolGeneralConfig = ({
  formRef,
  onValuesChangeCallback,
  instanceType,
}) => {
  const { t } = useTranslation();

  const [contextLoading, setContextLoading] = useState(true);
  const [nodegroupEnum, setNodegroupEnum] = useState({});
  const [nodegroupLoading, setNodegroupLoading] = useState(true);
  const [EKSNameEnum, setEKSNameEnum] = useState({});

  const { templateComponent } = useTemplateComponent({
    formRef,
    onValuesChangeCallback,
  });

  const {
    rootState: { eks: context },
  } = useResourceContext();

  const capacityInitialValue = instanceType === "hadoop" ? 5 : 2;

  useEffect(() => {
    const fetchSharedNodegroups = async () => {
      try {
        setNodegroupLoading(true);
        const response = await getSharedNodegroups();
        if (response.status === "OK") {
          const v = response.value.reduce((accumulator, item) => {
            accumulator[item.id] = { text: item.id };
            return accumulator;
          }, {});
          setNodegroupEnum(v);
          setNodegroupLoading(false);
        }
      } catch (error) {
        notification.error({
          description: t(
            "config.instance.error.nodegroup",
            "Fail to get shared nodegroup list from server.",
          ),
        });
      }
    };

    fetchSharedNodegroups();
  }, []);

  useEffect(() => {
    const fetchContext = async () => {
      if (context.isError) {
        setContextLoading(true);
        notification.error({
          description: t(
            "config.instance.error.context",
            "Fail to get context from server.",
          ),
        });
        return;
      }
      if (context.isLoading) {
        setContextLoading(true);
        notification.error({
          description: t(
            "config.nodegroup.error.contextLoading",
            "Loading context from server. Please re-launch nodegroup later.",
          ),
        });
        return;
      }

      const { eks } = context.data;
      setEKSNameEnum({ [eks]: { text: eks } });
      formRef.current.setFieldsValue({
        eks,
      });

      setContextLoading(false);
    };

    fetchContext();
  }, []);

  const rawSchema = [
    {
      title: t("config.pool.form.eksName", "EKS Name"),
      dataIndex: "eks",
      valueType: "select",
      valueEnum: EKSNameEnum,
      width: "400px",
      fieldProps: {
        disabled: true,
        loading: contextLoading,
      },
    },
    {
      title: t("config.pool.form.clusterType", "Cluster Type"),
      dataIndex: "type",
      valueType: "select",
      width: "400px",
      initialValue: instanceType,
      valueEnum: CLUSTER_TYPE_ENUM,
      fieldProps: {
        disabled: true,
      },
    },
    templateComponent(),
    {
      title: t("config.pool.form.nodegroup", "Nodegroup"),
      dataIndex: "nodegroup",
      valueType: "select",
      valueEnum: nodegroupEnum,
      fieldProps: {
        loading: nodegroupLoading,
      },
      formItemProps: {
        rules: [...requiredRule(t)],
      },

      width: "400px",
    },
    {
      title: t("config.pool.form.clusterName", "Cluster Name"),
      dataIndex: "name",
      valueType: "text",
      width: "400px",
      formItemProps: {
        rules: [...requiredRule(t)],
      },
    },
    {
      title: t("config.pool.form.capacity", "Capacity"),
      dataIndex: "capacity",
      valueType: "digit",
      initialValue: capacityInitialValue,
      width: "400px",
      formItemProps: {
        rules: [...requiredRule(t)],
      },
    },
  ];
  return rawSchema;
};
